import { useAppSelector } from "@/data/hooks";
import {
  responseTimeTerm,
  selectHasAutoSubmission,
  selectIsExam,
  selectIsTimedAssignment,
  selectVivaEnabled,
} from "@/features/assignment";
import {
  selectActiveExamLateSubmissionDate,
  selectActiveReadingDate,
  selectActiveWritingDate,
  selectFinalDate,
} from "@/features/timeline";
import { selectCanSubmit } from "@/stores/selectors";

import { ClockWithCountdown } from "./ClockWithCountdown";
import { PureClock } from "./PureClock";

interface Props {
  /** Render an inline version of the clock that is not floating on the page. */
  inline?: boolean;
}

/**
 * Redux connnected Clock which displays a countdown towards various dates
 * depending on the assignment and timeline setup:
 *
 *   1. Reading time clock during Exams
 *   2. Writing time clock during Exams and Timed assignments
 *   3. Late submission time clock during Exams
 *   4. Overtime clock message during Exams and Timed assignments
 *   5. Due date clock during assignments
 *
 */
export function Clock(props: Props) {
  const readingDate = useAppSelector(selectActiveReadingDate);
  const writingDate = useAppSelector(selectActiveWritingDate);
  const lateSubmissionDate = useAppSelector(selectActiveExamLateSubmissionDate);
  const hasAutoSubmission = useAppSelector(selectHasAutoSubmission);
  const canSubmit = useAppSelector(selectCanSubmit);

  const isExam = useAppSelector(selectIsExam);
  const isTimedAssignment = useAppSelector(selectIsTimedAssignment);
  const finalDate = useAppSelector(selectFinalDate);
  const vivaEnabled = useAppSelector(selectVivaEnabled);

  if (readingDate) {
    // The `key` prop is used to reset the countdown state when the date changes
    // from reading to writing.
    return (
      <ClockWithCountdown
        key={readingDate}
        endDate={readingDate}
        suffix="reading time remaining"
        inline={props.inline}
      />
    );
  }

  if (writingDate) {
    // The `key` prop is used to reset the countdown state when the date changes
    // from reading to writing.
    return (
      <ClockWithCountdown
        key={writingDate}
        endDate={writingDate}
        suffix={`${responseTimeTerm(vivaEnabled)} remaining`}
        triggerCountdownSeconds={hasAutoSubmission ? 15 : undefined}
        inline={props.inline}
      />
    );
  }

  if (isExam && !hasAutoSubmission && lateSubmissionDate && canSubmit) {
    return (
      <ClockWithCountdown
        key={lateSubmissionDate}
        endDate={lateSubmissionDate}
        suffix="late submission time remaining"
        inline={props.inline}
      />
    );
  }

  if (isExam && !hasAutoSubmission && canSubmit) {
    return (
      <PureClock message="Overtime" suffix="submission" inline={props.inline} />
    );
  }

  if (isTimedAssignment && canSubmit) {
    return (
      <PureClock message="Overtime" suffix="submission" inline={props.inline} />
    );
  }

  if (!isTimedAssignment && !isExam && finalDate) {
    return (
      <ClockWithCountdown
        key={finalDate}
        endDate={finalDate}
        suffix="until final due"
        inline={props.inline}
      />
    );
  }

  return null;
}
